// RunFlows.jsx — super-admin tab on the Automation Pipeline page.
// Trigger multi-step flows (social / email) for one brand or a group, toggle
// each scope between the LLM API and the Claude operator (global / per-flow /
// per-brand / per-step), and watch per-step status live.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { get, post } from "../../api";
import LlmProviderPanel from "./LlmProviderPanel";

const MODE_BTNS = [
  { v: "normal", label: "LLM API" },
  { v: "auto", label: "LLM→Claude" },
  { v: "terminal", label: "Claude operator" },
  { v: "inherit", label: "Inherit" },
];
const STEP_COLOR = { pending: "#9ca3af", running: "#2563eb", done: "#059669", failed: "#dc2626", skipped: "#b45309" };

// companies, globalMode, refreshOverview come from the parent so the dropdown
// data survives tab switches (the parent stays mounted).
export default function RunFlows({ companies = [], globalMode = "normal", refreshOverview, refreshPipeline, liveSchedulers }) {
  const [flows, setFlows] = useState([]);
  const [flowKey, setFlowKey] = useState("social");
  const [selected, setSelected] = useState({}); // brandId -> true
  const [period, setPeriod] = useState("day");
  const [quantity, setQuantity] = useState(1);
  const [recurring, setRecurring] = useState(true);
  // Default end date: today (YYYY-MM-DD for the date input) — a Run-Flows run
  // defaults to a single same-day run; pick a later date to recur further out.
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dryRun, setDryRun] = useState(false);
  const [runMode, setRunMode] = useState("inherit");
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [providers, setProviders] = useState({ selected: "siliconflow", text: [], image: [], video: [] }); // fed by LlmProviderPanel for the per-brand picker
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState(null);   // runId whose artifacts are shown
  const [artifacts, setArtifacts] = useState({});    // runId -> artifacts
  const [catalog, setCatalog] = useState({ triggered: [], scheduled: [], onDemand: [] });
  const [runsTab, setRunsTab] = useState("active");  // active | cancelled | deleted
  const [reconnecting, setReconnecting] = useState(false); // true while the backend is mid-restart (HTML/502 instead of JSON)

  const flow = useMemo(() => flows.find((f) => f.key === flowKey), [flows, flowKey]);
  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);

  // Schedulers known to do LLM (text) work → can run on the Claude operator.
  const LLM_SCHEDULERS = useMemo(() => new Set([
    "monthly-content-planner", "daily-content", "brand-enrichment", "email-sequence-planner",
    "email-sequence-drip", "newsletter", "crm-customer", "crm-prospect",
  ]), []);
  // Prefer the LIVE worker feed (real cadence + last run); fall back to the
  // static catalog if the worker overview hasn't loaded yet.
  const scheduledCards = useMemo(() => {
    if (liveSchedulers && liveSchedulers.length) {
      return liveSchedulers.map((s) => ({
        key: s.name,
        label: s.label || s.name,
        cadence: fmtCadence(s.intervalMs) + (s.lastRunAt ? ` · last ${fmtAgo(s.lastRunAt)}` : "") + (s.paused ? " · ⏸ paused" : ""),
        desc: s.description,
        llmKinds: [],
        provider: LLM_SCHEDULERS.has(s.name) ? "text" : "none",
      }));
    }
    return catalog.scheduled;
  }, [liveSchedulers, catalog.scheduled, LLM_SCHEDULERS]);

  // Bucket runs into the three sub-tabs so cancelled / artifact-deleted runs
  // don't clog the active list.
  const runBuckets = useMemo(() => {
    const active = [], cancelled = [], deleted = [];
    for (const r of runs) {
      if (r.artifactsDeletedAt) deleted.push(r);
      else if (r.status === "cancelled") cancelled.push(r);
      else active.push(r);
    }
    return { active, cancelled, deleted };
  }, [runs]);

  const load = useCallback(async () => {
    try { const defs = await get("/api/admin/flows/definitions"); setFlows((defs && defs.flows) || []); setReconnecting(false); }
    catch (e) { if (e && e.serverUnavailable) setReconnecting(true); else setErr((e && e.message) || "Failed to load"); }
    try { const c = await get("/api/admin/flows/automations"); if (c && c.success) setCatalog({ triggered: c.triggered || [], scheduled: c.scheduled || [], onDemand: c.onDemand || [] }); } catch (e) { /* */ }
  }, []);
  const loadRuns = useCallback(async () => {
    try { const r = await get("/api/admin/flows/runs?limit=40"); setRuns((r && r.runs) || []); setReconnecting(false); }
    catch (e) { if (e && e.serverUnavailable) setReconnecting(true); /* keep the last runs on screen */ }
  }, []);
  const toggleExpand = useCallback(async (runId) => {
    if (expanded === runId) { setExpanded(null); return; }
    setExpanded(runId);
    try { const r = await get(`/api/admin/flows/runs/${runId}/artifacts`); setArtifacts((m) => ({ ...m, [runId]: (r && r.artifacts) || {} })); } catch (e) { /* */ }
  }, [expanded]);

  // Refresh EVERYTHING after a per-run action: the runs list (+derived
  // statuses), the AI overview, and the parent's queue/scheduler/worker
  // panels — so all displayed statuses update accordingly.
  const refreshAll = useCallback(async (runId) => {
    await loadRuns();
    if (refreshOverview) refreshOverview();
    if (refreshPipeline) refreshPipeline();
    if (runId && expanded === runId) {
      try { const r = await get(`/api/admin/flows/runs/${runId}/artifacts`); setArtifacts((m) => ({ ...m, [runId]: (r && r.artifacts) || {} })); } catch (e) { /* */ }
    }
  }, [loadRuns, refreshOverview, refreshPipeline, expanded]);

  const runAction = useCallback(async (runId, path, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await post(`/api/admin/flows/runs/${runId}/${path}`, {});
      if (path === "delete-artifacts") setMsg(`Deleted ${r && r.total != null ? r.total : 0} artifact(s).`);
      await refreshAll(runId);
    } catch (e) { setErr((e && (e.error || e.message)) || `Failed: ${path}`); }
    finally { setBusy(false); }
  }, [refreshAll]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRuns(); const id = setInterval(loadRuns, 4000); return () => clearInterval(id); }, [loadRuns]);

  // Set the selected LLM provider for the currently-selected brands.
  const applyProviderToSelected = async (provider) => {
    if (!selectedIds.length) return setErr("Select brands first");
    setBusy(true); setErr(null); setMsg(null);
    try {
      for (const bid of selectedIds) {
        const co = companies.find((c) => c.brands.some((b) => b.brandId === bid));
        const bn = co && co.brands.find((b) => b.brandId === bid)?.name;
        await post("/api/admin/flows/provider", { scope: "brand", provider, brandId: bid, brandName: bn }, false);
      }
      setMsg(`Provider → ${provider} for ${selectedIds.length} brand(s).`);
      if (refreshOverview) refreshOverview();
    } catch (e) { setErr((e && (e.error || e.message)) || "Set provider failed"); }
    finally { setBusy(false); }
  };

  const toggleBrand = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectCompany = (co, on) => setSelected((s) => { const n = { ...s }; co.brands.forEach((b) => { n[b.brandId] = on; }); return n; });

  const run = async () => {
    if (!selectedIds.length) return setErr("Select at least one brand");
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await post("/api/admin/flows/run", { brandIds: selectedIds, flowKey, period, quantity: Number(quantity), recurring, endDate: recurring ? endDate : null, dryRun, providerMode: runMode }, false);
      if (r && r.err) throw new Error(r.err);
      setMsg(`${dryRun ? "Dry-run planned" : "Started"} ${r.started} run(s).`);
      await loadRuns();
    } catch (e) { setErr((e && e.message) || "Run failed"); }
    finally { setBusy(false); }
  };

  // Apply a provider mode to the selected brands at a given scope.
  const applyMode = async (scope, providerMode, extra = {}) => {
    if (scope !== "global" && !selectedIds.length) return setErr("Select brands first");
    setBusy(true); setErr(null);
    try {
      if (scope === "global") { await post("/api/admin/flows/provider-mode", { scope: "global", providerMode }, false); }
      else { for (const bid of selectedIds) { const co = companies.find((c) => c.brands.some((b) => b.brandId === bid)); const bn = co && co.brands.find((b) => b.brandId === bid)?.name; await post("/api/admin/flows/provider-mode", { scope, providerMode, brandId: bid, brandName: bn, flowKey, ...extra }, false); } }
      setMsg(`Set ${scope} → ${providerMode}${scope === "global" ? "" : ` for ${selectedIds.length} brand(s)`}.`);
      if (refreshOverview) refreshOverview();
    } catch (e) { setErr((e && e.message) || "Toggle failed"); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ fontSize: 13 }}>
      <style>{`
        .rf-sec{border:1px solid #e5e7eb;border-radius:10px;padding:12px 14px;margin-bottom:12px;background:#fff}
        .rf-h{font-weight:700;margin-bottom:8px}
        .rf-modebtn{border:1px solid #d1d5db;background:#fff;border-radius:6px;padding:3px 9px;font-size:12px;cursor:pointer;margin-right:4px}
        .rf-modebtn.on{background:#111827;color:#fff;border-color:#111827}
        .rf-co{border:1px solid #f0f0f0;border-radius:8px;margin-bottom:8px}
        .rf-co h6{margin:0;padding:6px 10px;background:#fafbfc;display:flex;justify-content:space-between;align-items:center;font-size:12px;font-weight:700}
        .rf-brand{display:inline-flex;align-items:center;gap:5px;padding:4px 10px;font-size:12px}
        .rf-chip{color:#fff;border-radius:999px;padding:1px 7px;font-size:10px;margin:1px 3px 1px 0;display:inline-block}
        .rf-run{border:1px solid #eee;border-radius:8px;padding:8px 10px;margin-bottom:6px}
        .rf-field{padding:5px 8px;border:1px solid #d1d5db;border-radius:6px;font-size:12px}
        .rf-prov{border:1px solid #e5e7eb;border-radius:8px;padding:6px 8px;min-width:200px;flex:1 1 200px;background:#fff}
      `}</style>

      {/* Global force toggle (top corner) */}
      <div className="rf-sec" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div><b>Global processing:</b> everything runs on <span style={{ fontWeight: 700 }}>{globalMode === "terminal" ? "Claude operator" : globalMode === "auto" ? "LLM→Claude" : "LLM API"}</span> unless a brand/flow/step overrides it.</div>
        <div>{MODE_BTNS.filter((m) => m.v !== "inherit").map((m) => (
          <button key={m.v} className={`rf-modebtn ${globalMode === m.v ? "on" : ""}`} disabled={busy} onClick={() => applyMode("global", m.v)}>{m.label}</button>
        ))}</div>
      </div>

      {/* Shared LLM provider panel: global provider select + ready badges + key
          management + SiliconFlow balance. Feeds `providers` to the per-brand
          picker below via onProvidersLoaded. */}
      <LlmProviderPanel onProvidersLoaded={setProviders} onChange={refreshOverview} />

      {reconnecting && (
        <div className="alert" style={{ background: "#fef3c7", border: "1px solid #fcd34d", color: "#92400e", display: "flex", alignItems: "center", gap: 8 }}>
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: 14, height: 14 }} />
          Reconnecting to the server… (it may be redeploying — your runs keep going)
        </div>
      )}
      {err && <div className="alert alert-warning">{err}</div>}
      {msg && <div className="alert alert-success">{msg}</div>}

      {/* ── On-demand end-to-end flows FIRST (the runnable section). The
          read-only Triggered + Scheduled catalogs render below the runs. ── */}
      <div className="rf-sec" style={{ background: "#f8fafc", borderColor: "#cbd5e1" }}>
        <div className="rf-h" style={{ marginBottom: 2 }}>▶ On-demand flows</div>
        <div style={{ fontSize: 11, color: "#64748b" }}>Full end-to-end jobs you start by hand below to drive a brand toward a goal. Each run is tracked with pause / cancel / delete-artifacts controls.</div>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {/* Left: pick flow + params */}
        <div className="rf-sec" style={{ flex: "1 1 280px" }}>
          <div className="rf-h">1 · Flow</div>
          <select className="rf-field" value={flowKey} onChange={(e) => setFlowKey(e.target.value)} style={{ width: "100%", marginBottom: 8 }}>
            {/* Runnable end-to-end flows in their own group. Any flow flagged
                manualEndToEnd lives here — including FAQ and Article, which are
                also auto-on-create but are still run by hand from this group. */}
            <optgroup label="▶ Manual end-to-end flows">
              {flows.filter((f) => f.manualEndToEnd || (f.group || "manual") === "manual").map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </optgroup>
            {/* Runnable triggered flow(s) that are NOT manual end-to-end — e.g.
                enrichment — plus the rest of the event-driven automations shown
                (disabled = run automatically). */}
            <optgroup label="⚡ Triggered">
              {flows.filter((f) => f.group === "triggered" && !f.manualEndToEnd).map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
              {catalog.triggered.map((a) => <option key={`t-${a.key}`} value={`t-${a.key}`} disabled>{a.label} — auto</option>)}
            </optgroup>
            {/* Scheduled automations — informational (managed on the Schedulers tab). */}
            <optgroup label="⏱ Scheduled (automatic)">
              {scheduledCards.map((a) => <option key={`s-${a.key}`} value={`s-${a.key}`} disabled>{a.label} — auto</option>)}
            </optgroup>
          </select>
          {flow && <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>{flow.desc}</div>}
          {flow && <div style={{ marginBottom: 8 }}>{flow.steps.map((s) => <span key={s.key} className="rf-chip" style={{ background: "#6b7280" }}>{s.label}</span>)}</div>}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {/* Qty per Period: how many artifacts (ideas / articles / sequences)
                this flow creates each period, recurring until the end date.
                Enrichment is one-time, so it has no qty/period/recurrence. */}
            {flowKey !== "enrichment" && (
              <>
                <label>Qty <input className="rf-field" type="number" min={1} style={{ width: 56 }} value={quantity} onChange={(e) => setQuantity(e.target.value)} title="Artifacts to create EACH period (e.g. 3 ideas / articles / sequences)" /></label>
                <label>per <select className="rf-field" value={period} onChange={(e) => setPeriod(e.target.value)} title="How often the quantity is produced">
                  <option value="day">day</option><option value="week">week</option><option value="month">month</option>
                </select></label>
                <label title="Repeat every period until the end date"><input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} /> Recurring</label>
                {recurring && (
                  <label>until <input className="rf-field" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} title="Repeat until this date (default: today)" /></label>
                )}
              </>
            )}
            <label><input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} /> Dry-run (no side effects)</label>
          </div>
          {flowKey !== "enrichment" && (
            <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
              {recurring
                ? `Creates ${quantity || 1} ${flowKey === "seo" ? "article(s)" : flowKey === "seo_keywords" ? "keyword research seed(s)" : flowKey === "email" ? "sequence(s)" : "idea(s)"} per ${period}, repeating until ${endDate}.`
                : `Runs once — creates ${quantity || 1} ${flowKey === "seo" ? "article(s)" : flowKey === "seo_keywords" ? "keyword research seed(s)" : flowKey === "email" ? "sequence(s)" : "idea(s)"}.`}
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            <span style={{ fontSize: 12, color: "#555" }}>Run on: </span>
            {MODE_BTNS.map((m) => <button key={m.v} className={`rf-modebtn ${runMode === m.v ? "on" : ""}`} onClick={() => setRunMode(m.v)}>{m.label}</button>)}
          </div>
          <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} disabled={busy || !selectedIds.length} onClick={run}>
            {busy ? "Working…" : `▶ Run ${flow ? flow.label : ""} for ${selectedIds.length} brand(s)`}
          </button>
        </div>

        {/* Right: brand/company multi-select + scoped toggles */}
        <div className="rf-sec" style={{ flex: "2 1 420px" }}>
          <div className="rf-h">2 · Brands ({selectedIds.length} selected)</div>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: "#555" }}>Set selected brands → </span>
            {MODE_BTNS.map((m) => <button key={m.v} className="rf-modebtn" disabled={busy} onClick={() => applyMode("brand", m.v)}>{m.label}</button>)}
            <span style={{ fontSize: 12, color: "#555", marginLeft: 10 }}>· {flow ? flow.label : ""} steps → </span>
            {MODE_BTNS.map((m) => <button key={m.v} className="rf-modebtn" disabled={busy} onClick={() => applyMode("flow", m.v)}>{m.label}</button>)}
          </div>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: "#555" }}>LLM provider for selected brands → </span>
            <select className="rf-field" disabled={busy} value="" onChange={(e) => { if (e.target.value) { applyProviderToSelected(e.target.value); e.target.value = ""; } }}>
              <option value="">choose…</option>
              {(providers.text || []).map((p) => <option key={p.id} value={p.id} disabled={!p.ready}>{p.label}{p.ready ? "" : " (no key)"}</option>)}
              <option value="inherit">Inherit (use global)</option>
            </select>
          </div>
          <div style={{ maxHeight: 300, overflow: "auto" }}>
            {companies.map((co) => (
              <div className="rf-co" key={co.companyId}>
                <h6><span>{co.companyName}</span>
                  <span><a href="#" onClick={(e) => { e.preventDefault(); selectCompany(co, true); }}>all</a> · <a href="#" onClick={(e) => { e.preventDefault(); selectCompany(co, false); }}>none</a></span>
                </h6>
                <div style={{ padding: "4px 6px" }}>
                  {co.brands.map((b) => (
                    <label className="rf-brand" key={b.brandId} style={b.disabled ? { opacity: 0.5 } : undefined} title={b.disabled ? "Brand is halted — flows are disabled until it's re-enabled" : undefined}>
                      <input type="checkbox" checked={!!selected[b.brandId] && !b.disabled} disabled={b.disabled} onChange={() => toggleBrand(b.brandId)} />
                      {b.name}
                      {b.disabled
                        ? <span className="rf-chip" style={{ background: "#b91c1c" }}>(disabled)</span>
                        : <span className="rf-chip" style={{ background: b.effectiveMode === "terminal" ? "#059669" : b.effectiveMode === "auto" ? "#2563eb" : "#9ca3af" }}>{b.modeLabel}</span>}
                    </label>
                  ))}
                  {co.brands.length === 0 && <span style={{ fontSize: 11, color: "#aaa" }}>no brands</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Runs — split into Active / Cancelled / Deleted-artifacts so a click
          moves a run out of the active list and it stops clogging the view. */}
      <div className="rf-sec">
        <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
          {[
            { k: "active", label: `Active runs (${runBuckets.active.length})` },
            { k: "cancelled", label: `Cancelled (${runBuckets.cancelled.length})` },
            { k: "deleted", label: `Deleted artifacts (${runBuckets.deleted.length})` },
          ].map((t) => (
            <button key={t.k} className={`rf-modebtn ${runsTab === t.k ? "on" : ""}`} onClick={() => setRunsTab(t.k)}>{t.label}</button>
          ))}
        </div>
        {(() => {
          const list = runBuckets[runsTab] || [];
          if (!list.length) return <div style={{ color: "#999", fontSize: 12 }}>{runsTab === "active" ? "No active runs." : runsTab === "cancelled" ? "No cancelled runs." : "No runs with deleted artifacts."}</div>;
          return list.map((r) => {
            const paused = r.status === "paused";
            const archived = runsTab !== "active";
            return (
              <div className="rf-run" key={r._id}>
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
                  <span><b>{r.brandName || r.brandId}</b> · {r.flowLabel || r.flowKey} · {r.quantity}/{r.period}{r.recurring ? ` ↻ until ${r.endDate ? String(r.endDate).slice(0, 10) : "?"}` : " (once)"} {r.dryRun && <em>(dry-run)</em>}</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ color: r.status === "completed" ? "#059669" : r.status === "failed" ? "#dc2626" : paused ? "#b45309" : r.status === "cancelled" ? "#6b7280" : "#2563eb", fontWeight: 600 }}>{r.status}</span>
                    {r.artifactsDeletedAt && <span style={{ fontSize: 10, color: "#dc2626" }}>🗑 {r.artifactsDeletedCount || 0} deleted · {fmtAgo(r.artifactsDeletedAt)}</span>}
                    <button className="rf-modebtn" onClick={() => toggleExpand(r._id)}>{expanded === r._id ? "Hide" : "Preview"}</button>
                    {!archived && !["completed", "failed", "cancelled"].includes(r.status) && (
                      <button className="rf-modebtn" disabled={busy} onClick={() => runAction(r._id, "pause")}>
                        {paused ? "▶ Resume" : "⏸ Pause"}
                      </button>
                    )}
                    {!archived && !["completed", "failed", "cancelled"].includes(r.status) && (
                      <button className="rf-modebtn" disabled={busy} style={{ color: "#b45309" }}
                        onClick={() => runAction(r._id, "cancel", `Cancel this ${r.flowLabel || r.flowKey} run for ${r.brandName || r.brandId}?`)}>
                        ✕ Cancel
                      </button>
                    )}
                    {!archived && (
                      <button className="rf-modebtn" disabled={busy || !paused} style={{ color: paused ? "#dc2626" : "#bbb" }}
                        title={paused ? "Soft-delete this run's artifacts (reversible)" : "Pause the run first to delete its artifacts"}
                        onClick={() => runAction(r._id, "delete-artifacts", `Soft-delete ALL artifacts this run created for ${r.brandName || r.brandId}? Reversible, but they disappear from the app.`)}>
                        🗑 Delete artifacts
                      </button>
                    )}
                  </span>
                </div>
                <div style={{ marginTop: 4 }}>
                  {(r.steps || []).map((s) => {
                    // Paused → recolor a running step amber + freeze its timer.
                    const color = paused && s.status === "running" ? "#b45309" : (STEP_COLOR[s.status] || "#9ca3af");
                    return (
                      <span key={s.key} className="rf-chip" style={{ background: color }} title={paused && s.status === "running" ? "paused" : s.status}>
                        {s.label}{s.count ? ` (${s.count})` : ""}{stepTimer(s, r)}
                      </span>
                    );
                  })}
                </div>
                {r.error && <div style={{ fontSize: 11, color: "#c0392b" }}>{r.error}</div>}
                {expanded === r._id && <ArtifactPreview data={artifacts[r._id]} />}
              </div>
            );
          });
        })()}
      </div>

      {/* ── Read-only catalogs, BELOW the runnable flows ── */}
      <CatalogSection
        title="⚡ Triggered flows"
        subtitle="Run automatically in reaction to an event (a brand is created, an email arrives, a post comes due). Not started by hand."
        items={catalog.triggered}
        kindOf="trigger"
      />
      <CatalogSection
        title="⏱ Scheduled automations"
        subtitle={(liveSchedulers && liveSchedulers.length)
          ? "Live from the worker — recurring jobs with their real cadence + last run. Pause / resume / run-now live on the Schedulers tab."
          : "Recurring jobs a worker scheduler runs on a cadence. Pause / resume / run-now live on the Schedulers tab."}
        items={scheduledCards}
        kindOf="cadence"
      />
    </div>
  );
}

// Humanize a scheduler interval (ms) and a "time ago" for last-run.
function fmtCadence(ms) {
  if (!ms) return "continuous";
  const s = Math.round(ms / 1000);
  if (s < 60) return `every ${s}s`;
  const m = Math.round(s / 60); if (m < 60) return `every ${m}m`;
  const h = Math.round(m / 60); if (h < 24) return `every ${h}h`;
  return `every ${Math.round(h / 24)}d`;
}
function fmtAgo(iso) {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const s = Math.round(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.round(s / 60); if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
  } catch (e) { return ""; }
}

// Read-only catalog of automations (triggered / scheduled). Each card shows
// what fires it (event or cadence), its LLM kinds, and whether it can run on
// the Claude operator (text) or is locked to a media model (image/video) or
// uses no LLM at all.
function CatalogSection({ title, subtitle, items, kindOf }) {
  if (!items || !items.length) return null;
  const badge = (a) => {
    if (a.provider === "image") return { t: "image/video model", c: "#7c3aed" };
    if (a.provider === "none") return { t: "no LLM", c: "#6b7280" };
    return { t: "LLM / Claude operator", c: "#059669" }; // text → controllable
  };
  return (
    <div className="rf-sec">
      <div className="rf-h" style={{ marginBottom: 2 }}>{title} <span style={{ color: "#9ca3af", fontWeight: 400 }}>({items.length})</span></div>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8 }}>{subtitle}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {items.map((a) => {
          const b = badge(a);
          return (
            <div key={a.key} style={{ width: 230, border: "1px solid #e6e8eb", borderRadius: 8, padding: "8px 10px", background: "#fff" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#222" }}>{a.label}</div>
              <div style={{ fontSize: 10, color: "#2563eb", marginTop: 2 }}>{kindOf === "trigger" ? `⚡ ${a.trigger}` : `⏱ ${a.cadence}`}</div>
              {a.desc && <div style={{ fontSize: 10, color: "#777", marginTop: 2 }}>{a.desc}</div>}
              {a.llmKinds && a.llmKinds.length > 0 && (
                <div style={{ fontSize: 10, color: "#888", marginTop: 3 }}>{a.llmKinds.join(", ")}</div>
              )}
              <div style={{ marginTop: 5 }}>
                <span style={{ fontSize: 9, color: "#fff", background: b.c, borderRadius: 999, padding: "1px 7px" }}>{b.t}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Per-step duration: live elapsed while running, final duration once done.
function fmtDur(sec) { if (sec < 60) return sec + "s"; const mn = Math.floor(sec / 60); return mn + "m" + (sec % 60) + "s"; }
function stepTimer(s, run) {
  try {
    if (s.status === "running" && s.startedAt) {
      // When the run is paused, freeze the elapsed at the moment it paused
      // instead of ticking live off Date.now().
      const nowMs = (run && run.status === "paused" && run.pausedAt) ? new Date(run.pausedAt).getTime() : Date.now();
      return " · " + fmtDur(Math.max(0, Math.round((nowMs - new Date(s.startedAt)) / 1000)));
    }
    if (s.status === "done" && s.startedAt && s.finishedAt) return " · " + fmtDur(Math.max(0, Math.round((new Date(s.finishedAt) - new Date(s.startedAt)) / 1000)));
  } catch (e) { /* */ }
  return "";
}

// ── Artifact helpers: turn a raw doc into a {kind,title,subtitle,thumb,snippet}
// card descriptor, and render its full content in the modal. ──────────────────
const firstUrl = (...vals) => {
  for (const v of vals) {
    if (Array.isArray(v) && v.length) return v[0];
    if (typeof v === "string" && v) return v;
  }
  return "";
};
const clip = (s, n) => { s = (s || "").toString().trim(); return s.length > n ? s.slice(0, n) + "…" : s; };

// Map each artifact type to a uniform card descriptor used by the miniatures.
function describe(kind, it) {
  switch (kind) {
    case "keywords": return {
      title: it.keyword || "(keyword)",
      subtitle: [it.intent, it.searchVolume != null ? it.searchVolume + " vol" : null, it.difficultyScore != null ? "KD " + it.difficultyScore : null].filter(Boolean).join(" · "),
      snippet: it.topicCluster ? "cluster: " + it.topicCluster : "",
    };
    case "ideas": return {
      title: it.title || "(untitled idea)",
      subtitle: it.status || it.queueStatus || "",
      snippet: clip(it.generatedContent || it.description || it.prompt, 220),
    };
    case "briefs": return {
      title: it.title || "(brief)",
      subtitle: [(it.headings || []).length + " headings", it.targetWordCount ? it.targetWordCount + "w target" : null, it.status].filter(Boolean).join(" · "),
      snippet: clip((it.headings || []).map((h) => h.text).filter(Boolean).join(" • "), 220),
    };
    case "articles": return {
      title: it.title || "(article)",
      subtitle: [(it.wordCount || 0) + "w", it.status].filter(Boolean).join(" · "),
      snippet: clip(it.excerpt || it.metaDescription || it.bodyMarkdown, 220),
      thumb: firstUrl(it.imageUrls),
    };
    case "posts": return {
      title: clip(it.textContent || it.imagePrompt || "(post)", 70) || "(media post)",
      subtitle: [it.contentType, it.queueStatus].filter(Boolean).join(" · "),
      snippet: clip(it.textContent, 220),
      thumb: firstUrl(it.imageUrl, it.imageContent),
    };
    case "sequences": return {
      title: it.name || "(sequence)",
      subtitle: [(it.steps || []).length + " emails", it.status, it.source].filter(Boolean).join(" · "),
      snippet: clip(it.goal || (it.steps || []).map((s) => s.subject).filter(Boolean).join(" • "), 220),
    };
    case "faqs": {
      const answers = it.answers || [];
      const published = answers.find((a) => a.status === "published");
      const best = published || answers[0];
      return {
        title: clip(it.question || "(question)", 70),
        subtitle: [it.source, answers.length + " answer" + (answers.length === 1 ? "" : "s"), it.hasPublishedAnswer ? "✓ published" : null].filter(Boolean).join(" · "),
        snippet: clip(best ? best.bodyMarkdown : "(no answer yet)", 220),
      };
    }
    case "profile": {
      const status = it.lastEnrichmentError ? "⚠ error" : it.pendingOperator ? "⏳ waiting for operator" : it.enrichedAt ? "✓ enriched" : "pending";
      return {
        title: "Brand profile",
        subtitle: [status, it.confidence != null ? `conf ${Math.round(it.confidence * 100)}%` : null, it.pages ? `${it.pages} pages` : null].filter(Boolean).join(" · "),
        snippet: it.lastEnrichmentError ? clip(it.lastEnrichmentError, 200) : clip(it.summary || it.bigIdea, 220),
      };
    }
    default: return { title: "(item)", subtitle: "", snippet: "" };
  }
}

// Full content rendered inside the resizable modal.
function ArtifactDetail({ kind, it }) {
  const L = (label, val) => val ? <div style={{ marginBottom: 6 }}><b style={{ color: "#555" }}>{label}: </b>{val}</div> : null;
  const pre = (txt) => <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 13, lineHeight: 1.5, margin: 0, fontFamily: "inherit" }}>{txt}</pre>;
  const thumb = kind === "posts" ? firstUrl(it.imageUrl, it.imageContent) : kind === "articles" ? firstUrl(it.imageUrls) : "";
  return (
    <div>
      {thumb ? <img src={thumb} alt="" style={{ maxWidth: "100%", maxHeight: 320, objectFit: "contain", borderRadius: 8, marginBottom: 10, display: "block" }} /> : null}
      {kind === "keywords" && (<div>{L("Keyword", it.keyword)}{L("Intent", it.intent)}{L("Volume", it.searchVolume)}{L("Difficulty", it.difficultyScore)}{L("CPC", it.cpc)}{L("Cluster", it.topicCluster)}</div>)}
      {kind === "ideas" && (<div>{L("Status", it.status || it.queueStatus)}{L("Prompt", it.prompt)}<div style={{ marginTop: 8, fontWeight: 700, color: "#555" }}>Content</div>{pre(it.generatedContent || it.description || "(no content yet)")}</div>)}
      {kind === "briefs" && (<div>{L("Primary keyword", it.primaryKeyword)}{L("Secondary", (it.secondaryKeywords || []).join(", "))}{L("Target", (it.targetWordCount || 0) + " words")}{L("Status", it.status)}
        <div style={{ marginTop: 8, fontWeight: 700, color: "#555" }}>Outline</div>
        <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>{(it.headings || []).map((h, i) => <li key={i} style={{ fontSize: 13, marginLeft: (h.level === "h3" ? 14 : 0) }}><b>{(h.level || "h2").toUpperCase()}</b> {h.text}{h.notes ? <span style={{ color: "#888" }}> — {h.notes}</span> : null}</li>)}</ul>
        {(it.internalLinkSuggestions || []).length ? <><div style={{ marginTop: 8, fontWeight: 700, color: "#555" }}>Internal links</div><ul style={{ margin: "4px 0 0 18px" }}>{it.internalLinkSuggestions.map((l, i) => <li key={i} style={{ fontSize: 12 }}>{l.label || l.url} — {l.url}</li>)}</ul></> : null}</div>)}
      {kind === "articles" && (<div>{L("Status", it.status)}{L("Words", it.wordCount)}{L("Slug", it.slug)}{L("Meta title", it.metaTitle)}{L("Meta description", it.metaDescription)}{it.publishedUrl ? <div style={{ marginBottom: 6 }}><a href={it.publishedUrl} target="_blank" rel="noreferrer">View published →</a></div> : null}
        <div style={{ marginTop: 8, fontWeight: 700, color: "#555" }}>Body</div>{pre(it.bodyMarkdown || it.excerpt || "(no body yet)")}</div>)}
      {kind === "posts" && (<div>{L("Type", it.contentType)}{L("Status", it.queueStatus)}{L("Scheduled", it.scheduled_at ? new Date(it.scheduled_at).toLocaleString() : null)}{L("Image prompt", it.imagePrompt)}
        <div style={{ marginTop: 8, fontWeight: 700, color: "#555" }}>Caption</div>{pre(it.textContent || "(no text)")}</div>)}
      {kind === "sequences" && (<div>{L("Goal", it.goal)}{L("Status", it.status)}{L("Source", it.source)}
        {(it.steps || []).map((s, i) => <div key={i} style={{ borderTop: "1px solid #eee", marginTop: 8, paddingTop: 8 }}><b>#{(s.order ?? i) + 1} {s.subject || "(no subject)"}</b><div style={{ fontSize: 11, color: "#888" }}>after {s.delayValue || 0} {s.delayUnit || "hours"}</div>{pre(s.bodyText || "(no body)")}</div>)}</div>)}
      {kind === "faqs" && (<div>{L("Source", it.source)}{L("Primary keyword", it.primaryKeyword)}{L("Published", it.hasPublishedAnswer ? "yes" : "no")}{(it.tags || []).length ? L("Tags", it.tags.join(", ")) : null}{it.sourceUrl ? <div style={{ marginBottom: 6 }}><a href={it.sourceUrl} target="_blank" rel="noreferrer">Source link →</a></div> : null}
        <div style={{ marginTop: 8, fontWeight: 700, color: "#555" }}>Question</div>{pre(it.question || "(no question)")}
        <div style={{ marginTop: 8, fontWeight: 700, color: "#555" }}>Answers ({(it.answers || []).length})</div>
        {(it.answers || []).length ? (it.answers || []).map((a, i) => <div key={i} style={{ borderTop: "1px solid #eee", marginTop: 8, paddingTop: 8 }}><b>#{i + 1} {a.status || "draft"}{a.status === "published" ? " ✓" : ""}</b>{a.generatedBy ? <span style={{ fontSize: 11, color: "#888" }}> · {a.generatedBy}</span> : null}{pre(a.bodyMarkdown || "(no answer body)")}</div>) : pre("(no answers yet)")}</div>)}
      {kind === "profile" && (<div>
        {L("Status", it.lastEnrichmentError ? "⚠ error" : it.pendingOperator ? `⏳ waiting for Claude operator (${it.pendingOperator} task${it.pendingOperator > 1 ? "s" : ""})` : it.enrichedAt ? "✓ enriched" : "pending")}
        {it.enrichedAt && L("Enriched at", new Date(it.enrichedAt).toLocaleString())}
        {it.lastEnrichmentError && L("Last error", it.lastEnrichmentError)}
        {it.confidence != null && L("Confidence", Math.round(it.confidence * 100) + "%")}
        {it.pages && L("Website pages crawled", it.pages)}
        {L("Domain", it.domain)}{L("Big idea", it.bigIdea)}{L("Tone", it.tone)}{L("Style", it.style)}
        {L("Positioning", it.positioningStatement)}{L("Voice guidelines", it.voiceGuidelines)}
        {(it.contentPillars || []).length ? L("Content pillars", it.contentPillars.join(", ")) : null}
        {(it.valueProps || []).length ? L("Value props", it.valueProps.join(", ")) : null}
        {(it.differentiators || []).length ? L("Differentiators", it.differentiators.join(", ")) : null}
        {(it.targetCustomers || []).length ? L("Target customers", it.targetCustomers.join(", ")) : null}
        {(it.format || []).length ? L("Formats", it.format.join(", ")) : null}
        {(it.colors || []).length ? L("Palette", it.colors.join(" ")) : null}
        {it.summary ? <><div style={{ marginTop: 8, fontWeight: 700, color: "#555" }}>Summary</div>{pre(it.summary)}</> : null}
        {!it.enrichedAt && !it.lastEnrichmentError ? <div style={{ marginTop: 8, color: "#b45309", fontSize: 12 }}>No profile yet — the synthesis call is {it.pendingOperator ? "parked for the Claude operator to answer" : "still running"}. Drain it from AI Operations to complete.</div> : null}
      </div>)}
    </div>
  );
}

// Big resizable modal — drag the bottom-right grip (CSS resize) to resize.
function ArtifactModal({ kind, it, label, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 10, boxShadow: "0 10px 40px rgba(0,0,0,0.3)", width: "min(720px, 92vw)", height: "min(640px, 86vh)", minWidth: 320, minHeight: 240, maxWidth: "96vw", maxHeight: "94vh", resize: "both", overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: "1px solid #eef0f2" }}>
          <div style={{ fontWeight: 700, fontSize: 14, textTransform: "capitalize" }}>{label}</div>
          <button className="rf-modebtn" onClick={onClose}>Close ✕</button>
        </div>
        <div style={{ padding: 16, overflow: "auto", flex: 1 }}><ArtifactDetail kind={kind} it={it} /></div>
        <div style={{ fontSize: 10, color: "#bbb", textAlign: "right", padding: "0 8px 4px" }}>drag corner to resize ⤡</div>
      </div>
    </div>
  );
}

// Preview of the data each step created — clickable miniatures per artifact;
// click one to open the full content in a big resizable modal.
function ArtifactPreview({ data }) {
  const [open, setOpen] = useState(null); // { kind, it, label }
  if (!data) return <div style={{ fontSize: 12, color: "#999", marginTop: 6 }}>Loading preview…</div>;
  const has = Object.values(data).some((v) => Array.isArray(v) && v.length);
  if (!has) return <div style={{ fontSize: 12, color: "#999", marginTop: 6 }}>Nothing created yet for this run.</div>;

  const section = (kind, label, items) => (items && items.length ? (
    <div style={{ marginTop: 8 }}>
      {/* Sticky header: as you scroll a long artifact list, the current step's
          label stays pinned at the top, and the next step's header pushes it up. */}
      <div style={{ position: "sticky", top: 0, zIndex: 2, background: "#fafbfc", fontSize: 11, fontWeight: 700, color: "#555", padding: "4px 0", marginBottom: 4, borderBottom: "1px solid #eef0f2" }}>{label} ({items.length})</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {items.map((it, i) => {
          const d = describe(kind, it);
          return (
            <button key={i} onClick={() => setOpen({ kind, it, label: d.title })} title="Click to expand"
              style={{ textAlign: "left", width: 170, height: 104, border: "1px solid #e6e8eb", borderRadius: 8, background: "#fff", padding: 0, overflow: "hidden", cursor: "pointer", display: "flex", flexDirection: "column" }}>
              {d.thumb ? <img src={d.thumb} alt="" style={{ width: "100%", height: 42, objectFit: "cover" }} /> : null}
              <div style={{ padding: "6px 8px", overflow: "hidden", flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#222", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.title}</div>
                {d.subtitle ? <div style={{ fontSize: 10, color: "#2563eb", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.subtitle}</div> : null}
                {!d.thumb && d.snippet ? <div style={{ fontSize: 10, color: "#888", marginTop: 3, lineHeight: 1.3, maxHeight: 38, overflow: "hidden" }}>{d.snippet}</div> : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  ) : null);

  return (
    // Scrollable so EVERY artifact is reachable no matter how many a run made;
    // sticky section headers (above) keep the current step labelled while scrolling.
    <div style={{ marginTop: 8, background: "#fafbfc", border: "1px solid #eef0f2", borderRadius: 8, padding: "8px 10px", maxHeight: "60vh", overflowY: "auto", position: "relative" }}>
      {section("profile", "Brand profile", data.profile)}
      {section("keywords", "Keywords", data.keywords)}
      {section("ideas", "Ideas", data.ideas)}
      {section("briefs", "Briefs", data.briefs)}
      {section("articles", "Articles", data.articles)}
      {section("posts", "Posts", data.posts)}
      {section("sequences", "Sequences", data.sequences)}
      {section("faqs", "FAQ questions & answers", data.faqs)}
      {open ? <ArtifactModal kind={open.kind} it={open.it} label={open.label} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}
